/**
 * Lightweight Tabs primitive (RLY-024).
 *
 * Phase-1 polish bar: we don't need the full ARIA/focus-trap surface that
 * `@radix-ui/react-tabs` provides. This implementation:
 *   - exposes the same `<Tabs value onValueChange><TabsList><TabsTrigger />…
 *     <TabsContent /></Tabs>` API,
 *   - sets `role="tablist"`/`tab`/`tabpanel`,
 *   - manages keyboard focus via the buttons' default Tab/Enter behavior
 *     (left/right arrow nav lands in a Sprint 07+ a11y polish story).
 *
 * Replace with `@radix-ui/react-tabs` when accessibility audits require
 * arrow-key navigation between triggers.
 */
import * as React from "react";

import { cn } from "@/lib/utils";

interface TabsContextValue {
  value: string;
  onValueChange: (next: string) => void;
  idPrefix: string;
}

const TabsContext = React.createContext<TabsContextValue | null>(null);

function useTabsContext(component: string): TabsContextValue {
  const ctx = React.useContext(TabsContext);
  if (!ctx) throw new Error(`<${component}> must be rendered inside <Tabs>.`);
  return ctx;
}

export interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  onValueChange: (next: string) => void;
}

export function Tabs({ value, onValueChange, className, children, ...rest }: TabsProps) {
  const id = React.useId();
  const ctx = React.useMemo<TabsContextValue>(
    () => ({ value, onValueChange, idPrefix: id }),
    [value, onValueChange, id],
  );
  return (
    <TabsContext.Provider value={ctx}>
      <div className={cn("w-full", className)} {...rest}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

export function TabsList({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      role="tablist"
      className={cn(
        "inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

export interface TabsTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
}

export function TabsTrigger({ value, className, ...props }: TabsTriggerProps) {
  const ctx = useTabsContext("TabsTrigger");
  const active = ctx.value === value;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-controls={`${ctx.idPrefix}-panel-${value}`}
      id={`${ctx.idPrefix}-trigger-${value}`}
      data-state={active ? "active" : "inactive"}
      onClick={() => ctx.onValueChange(value)}
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
        active ? "bg-background text-foreground shadow-sm" : "",
        className,
      )}
      {...props}
    />
  );
}

export interface TabsContentProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
}

export function TabsContent({ value, className, children, ...rest }: TabsContentProps) {
  const ctx = useTabsContext("TabsContent");
  if (ctx.value !== value) return null;
  return (
    <div
      role="tabpanel"
      id={`${ctx.idPrefix}-panel-${value}`}
      aria-labelledby={`${ctx.idPrefix}-trigger-${value}`}
      className={cn("mt-4 ring-offset-background focus-visible:outline-none", className)}
      {...rest}
    >
      {children}
    </div>
  );
}
